import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router';
import { z } from 'zod';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import { useAuth } from '~/contexts/AuthContext/AuthProvider';
import { useUser } from '~/contexts/UserContext/UserProvider';
import {
  ApplicationStatus,
  BankConnectionResult,
  CountryCode,
  FlowTypes,
} from '~/enums';
import {
  formatAmountWithoutCurrency,
  formatPersonalNumber,
} from '~/helpers/formatters.helper';
import { logToSentry } from '~/helpers/loggers.helper';
import {
  getLanguageBasedOnReferrerUrl,
  getLanguageFromLocalStorage,
} from '~/helpers/market.helper';
import { getMarketBasedOnHostname } from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useQueryParams from '~/hooks/useQueryParams';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  getClientCompanies,
  getLoanApplication,
  patchApplication,
} from '~/store/actions/loanApplicationActions';
import {
  defaultLanguageToMarket,
  setLanguage,
  setMarket,
} from '~/store/slices/intl.slice';
import {
  removeClearedState,
  setBankConnectionError,
  setCompanyIsNew,
  updateApplicantData,
  updateCurrentStepNumber,
  updateFlowType,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import { theme } from '~/styles/themes';
import { ConfirmationPage } from './components/ConfirmationPage/ConfirmationPage';
import LaunchView from './components/LaunchView/LaunchView';
import Steps from './components/Steps/Steps';
import { UserInfo } from '~/store/apis/endpoints/userManagementApi/userManagement.api';

const onboardingMarkets = [
  CountryCode.SE,
  CountryCode.FI,
  CountryCode.NL,
  CountryCode.NO,
  CountryCode.BE,
  CountryCode.DK,
  CountryCode.DE,
];

// NOTE: Use this to test a new market in onboarding in non-prod environments
// if (import.meta.env.MODE !== 'production') {
//   onboardingMarkets.push();
// }

const BANK_CONNECTION_RESULT_QUERY_STRING_KEY = 'bank_connection_result';

const LoanApplication = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [ready, setReady] = useState(false);
  const [pageIsRefreshing, setPageIsRefreshing] = useState(false);
  const hasHandledInitialStateRef = useRef(false);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { user, companies, hasUserInfoError } = useUser();
  const queryParams = useQueryParams();
  const marketFromDomain = getMarketBasedOnHostname();
  const { loanApplication, intl } = useSelector((state: RootState) => state);
  const {
    flowType,
    applicationStatus,
    currentStepNumber,
    form,
  } = loanApplication;

  const { market } = intl;

  const applicationUuidParam = queryParams.get('applicationUuid');
  const loanAmountParam = queryParams.get('loan_amount');
  const eskatParam = queryParams.get('eskat');
  const bankConnectionResultParam = queryParams.get(
    BANK_CONNECTION_RESULT_QUERY_STRING_KEY
  );

  useEffect(() => {
    if (loanAmountParam) {
      dispatch(
        updateForm({
          amount: formatAmountWithoutCurrency(loanAmountParam),
        })
      );
      // A loan amount in the URL (e.g. from the website's loan calculator)
      // skips the LaunchView and goes straight to step 1.
      dispatch(updateCurrentStepNumber(1));
    }
  }, []);

  useEffect(() => {
    if (marketFromDomain) {
      dispatch(setMarket(marketFromDomain));
      const languageFromReferrerUrl = getLanguageBasedOnReferrerUrl();
      const languageFromLocalStorage = getLanguageFromLocalStorage();
      const language = languageFromLocalStorage ?? languageFromReferrerUrl;
      if (language) {
        dispatch(setLanguage(language));
      } else {
        dispatch(defaultLanguageToMarket(marketFromDomain));
      }
    } else {
      window.location.replace(
        window.location.href.replace('.com', `.${CountryCode.SE}`)
      );
      setPageIsRefreshing(true);
    }
  }, []);

  const syncApplicantWithLoggedInUser = (userInfo: UserInfo) => {
    dispatch(
      updateApplicantData({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        phone: userInfo.phone,
        email: userInfo.email,
        personalNumber: userInfo?.personalNumber
          ? formatPersonalNumber(userInfo.personalNumber, market)
          : userInfo?.personalNumber,
        dateOfBirth: userInfo?.dateOfBirth,
      })
    );
  };

  useEffect(() => {
    if (user) {
      syncApplicantWithLoggedInUser(user);
    }
  }, [user]);

  const handleAuthenticationStatus = () => {
    dispatch(
      updateForm({
        applicantIdentified: isAuthenticated,
        applicantAuthenticated: isAuthenticated,
      })
    );

    if (isAuthenticated && user?.qcode) {
      dispatch(updateForm({ userCode: user.qcode }));
    }
  };

  // The flow type always follows the auth state. Auth resolves after this
  // page has mounted (both Auth0 and Cognito start out logged out), and
  // clearLoanApplicationState resets flowType to Manual, so it is re-synced
  // whenever either changes rather than decided once on mount.
  useEffect(() => {
    const expectedFlowType = isAuthenticated
      ? FlowTypes.Authenticated
      : FlowTypes.Manual;
    if (flowType !== expectedFlowType) {
      dispatch(updateFlowType(expectedFlowType));
    }
  }, [flowType, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      handleAuthenticationStatus();
    }
  }, [isAuthenticated, user?.qcode]);

  useEffect(() => {
    const handleAuthenticatedState = async () => {
      handleAuthenticationStatus();

      // NOTE: this needs to be done before final patch
      // if its done after final patch, we will not have uuid when doing the final patch.
      // Any problem with doing this before handling url auth type?
      if (applicationUuidParam) {
        await dispatch(getLoanApplication(applicationUuidParam));
      }

      if (eskatParam && applicationUuidParam) {
        sessionStorage.removeItem('loan-application-uuid');
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('eskat');
        history.replace({ search: currentUrl.search });
        if (eskatParam === 'connected') {
          dispatch(
            pushToGtmLoanAction({
              actionName: 'eskat_connected',
            })
          );
          dispatch(updateForm({ eskatConnected: true }));
          await dispatch(patchApplication({ currentStepCompleted: false }));
        } else {
          dispatch(
            pushToGtmLoanAction({
              actionName: 'eskat_connect_cancelled',
            })
          );
        }
      }

      if (bankConnectionResultParam && applicationUuidParam) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete(BANK_CONNECTION_RESULT_QUERY_STRING_KEY);
        history.replace({ search: currentUrl.search });

        switch (bankConnectionResultParam) {
          case BankConnectionResult.Success:
            dispatch(updateForm({ bankConnected: true }));
            await dispatch(patchApplication({ currentStepCompleted: false }));
            break;
          case BankConnectionResult.Error:
          case BankConnectionResult.Cancelled:
            dispatch(setBankConnectionError(true));
            break;
          default:
            dispatch(setBankConnectionError(true));
            logToSentry(
              Error('Unknown bank_connection_result param'),
              `Unknown bank_connection_result param ${bankConnectionResultParam}`
            );
        }
        const isValidResultParam = z
          .nativeEnum(BankConnectionResult)
          .safeParse(bankConnectionResultParam).success;

        if (isValidResultParam) {
          dispatch(
            pushToGtmLoanAction({
              actionName: `bank_connection_${bankConnectionResultParam}`,
            })
          );
        }
      }

      setReady(true);
    };

    // A logged-in user's only token comes from the auth provider, which
    // registers it just before isLoading turns false. Fetching the
    // application before that fails with "No ACCESS_TOKEN found!" on reload.
    if (isAuthLoading || hasHandledInitialStateRef.current) return;
    hasHandledInitialStateRef.current = true;
    handleAuthenticatedState();
  }, [isAuthLoading]);

  /**
   * Add applicationUuid to url when its set in redux.
   */
  useEffect(() => {
    const { applicationUuid } = loanApplication.form;
    if (applicationUuid) {
      const url = new URL(window.location.href);
      url.searchParams.set('applicationUuid', applicationUuid);
      history.replace({ search: url.search });
    }
  }, [loanApplication.form.applicationUuid]);

  const loadClientCompaniesForUser = () => {
    // Without the user's companies there is nothing to pick from, so fall
    // back to the company search rather than leaving step 1 without one.
    if (hasUserInfoError) {
      dispatch(setCompanyIsNew(true));
      return;
    }
    // Still waiting for the user's companies.
    if (companies === undefined) return;
    if (companies.length) {
      dispatch(getClientCompanies());
    } else {
      dispatch(setCompanyIsNew(true));
    }
  };

  /**
   * Remove 'applicationUuid' from URL if action 'clearLoanApplicationState' has been called.
   */
  useEffect(() => {
    if (loanApplication.cleared) {
      const url = new URL(window.location.href);
      url.searchParams.delete('applicationUuid');
      history.replace({ search: url.search });
      dispatch(removeClearedState());
      document.cookie = `publicToken=; Max-Age=0`;
      if (isAuthenticated) {
        if (user) {
          syncApplicantWithLoggedInUser(user);
        }
        // Clearing wipes the loaded clients and companyIsNew, which would
        // leave step 1 with no company field at all.
        loadClientCompaniesForUser();
      }
      handleAuthenticationStatus();
    }
  }, [loanApplication.cleared]);

  const viewHandler = useMemo(() => {
    const shouldShowSteps =
      currentStepNumber === 1 ||
      (currentStepNumber > 1 && form.applicationUuid);
    switch (applicationStatus) {
      case ApplicationStatus.None:
        if (shouldShowSteps) {
          return <Steps />;
        }
        return <LaunchView />;
      case ApplicationStatus.ApplicationFinished:
      case ApplicationStatus.ApplicationUnderReview:
      case ApplicationStatus.OfferNotApproved:
      case ApplicationStatus.OfferApproved:
        return <ConfirmationPage />;

      default:
        logToSentry(
          Error('viewHandler switch ended up in default'),
          `viewHandler in Onboarding.tsx, applicationStatus is ${applicationStatus}`
        );
        return <LaunchView />;
    }
  }, [applicationStatus, flowType, currentStepNumber, form.applicationUuid]);

  useEffect(() => {
    if (isAuthenticated) {
      loadClientCompaniesForUser();
    }
  }, [isAuthenticated, companies, hasUserInfoError]);

  const isMarketValid = useMemo(
    () =>
      onboardingMarkets.some(
        (availableMarket: CountryCode) => availableMarket === market
      ),
    [market]
  );

  useEffect(() => {
    if (!isMarketValid) {
      logToSentry(
        Error(`Unsupported market for onboarding`),
        `Unsupported onboarding market ${market}`
      );
    }
  }, [isMarketValid]);

  return isMarketValid ? (
    <>
      {ready && !pageIsRefreshing ? (
        <>{viewHandler}</>
      ) : (
        <SpinnerCircular color={theme.colors.secondaryGray} />
      )}
    </>
  ) : (
    <Redirect to="/404" />
  );
};

export default LoanApplication;
